import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import {
  createAiImagePreset,
  deleteAiImagePreset,
  fetchAiImagePromptSettings,
  fetchAllAiImagePresets,
  updateAiImagePreset,
  updateAiImagePromptSetting,
} from '../aiImageStudioApi';
import {
  AiImagePreset,
  AiImagePresetInput,
  AiImagePromptSetting,
  AiImagePromptSettingInput,
} from '../types';
import StudioActionDialog from './StudioActionDialog';

type PresetScope = 'general' | 'recipe' | 'wine';
type SettingsTab = PresetScope | 'optimize';

type PresetFormState = {
  _id?: string;
  name: string;
  promptTemplate: string;
  scope: PresetScope;
  appendMode: 'replace' | 'append' | 'prepend';
  isActive: boolean;
  sortOrder: number;
};

type OptimizeFormState = {
  key: string;
  name: string;
  description?: string;
  promptText: string;
};

interface PromptSettingsDialogProps {
  open: boolean;
  initialTab?: SettingsTab;
  onClose: () => void;
  onPresetsChanged: () => Promise<void>;
}

type PendingNavigation =
  | { type: 'close' }
  | { type: 'tab'; tab: SettingsTab }
  | { type: 'preset'; preset: AiImagePreset | null };

function presetSignature(form: PresetFormState): string {
  return JSON.stringify({
    id: form._id || '',
    name: form.name,
    promptTemplate: form.promptTemplate,
    scope: form.scope,
    appendMode: form.appendMode,
    isActive: form.isActive,
    sortOrder: form.sortOrder,
  });
}

function optimizeSignature(form: OptimizeFormState): string {
  return JSON.stringify({
    name: form.name,
    description: form.description || '',
    promptText: form.promptText,
  });
}

const OPTIMIZE_PROMPT_KEY = 'optimizePrompt';

function presetScope(scope?: string): PresetScope {
  if (scope === 'recipe' || scope === 'wine') return scope;
  return 'general';
}

function scopeForTab(tab: SettingsTab): PresetScope {
  return tab === 'optimize' ? 'general' : tab;
}

function defaultAppendMode(scope: PresetScope): PresetFormState['appendMode'] {
  return scope === 'general' ? 'append' : 'replace';
}

function createEmptyPresetForm(scope: PresetScope): PresetFormState {
  return {
    name: '',
    promptTemplate: '',
    scope,
    appendMode: defaultAppendMode(scope),
    isActive: true,
    sortOrder: 0,
  };
}

function presetToForm(preset: AiImagePreset): PresetFormState {
  const scope = presetScope(preset.scope);
  return {
    _id: preset._id,
    name: preset.name,
    promptTemplate: preset.promptTemplate,
    scope,
    appendMode: preset.appendMode || defaultAppendMode(scope),
    isActive: preset.isActive,
    sortOrder: preset.sortOrder || 0,
  };
}

function settingToForm(setting?: AiImagePromptSetting | null): OptimizeFormState {
  return {
    key: setting?.key || OPTIMIZE_PROMPT_KEY,
    name: setting?.name || 'Optimera prompt med AI',
    description: setting?.description,
    promptText: setting?.promptText || '',
  };
}

function presetPayload(form: PresetFormState): AiImagePresetInput {
  return {
    name: form.name.trim(),
    scope: form.scope,
    promptTemplate: form.promptTemplate.trim(),
    appendMode: form.appendMode,
    isActive: form.isActive,
    sortOrder: Number.isFinite(form.sortOrder) ? form.sortOrder : 0,
  };
}

function optimizePayload(form: OptimizeFormState): AiImagePromptSettingInput {
  return {
    name: form.name.trim(),
    description: form.description?.trim() || undefined,
    promptText: form.promptText.trim(),
  };
}

const PromptSettingsDialog: React.FC<PromptSettingsDialogProps> = ({
  open,
  initialTab = 'general',
  onClose,
  onPresetsChanged,
}) => {
  const [tab, setTab] = useState<SettingsTab>('general');
  const [presets, setPresets] = useState<AiImagePreset[]>([]);
  const [promptSettings, setPromptSettings] = useState<AiImagePromptSetting[]>([]);
  const [presetForm, setPresetForm] = useState<PresetFormState>(() => createEmptyPresetForm('general'));
  const [optimizeForm, setOptimizeForm] = useState<OptimizeFormState>(() => settingToForm(null));
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [presetBaseline, setPresetBaseline] = useState(() => presetSignature(createEmptyPresetForm('general')));
  const [optimizeBaseline, setOptimizeBaseline] = useState(() => optimizeSignature(settingToForm(null)));
  const [pendingNavigation, setPendingNavigation] = useState<PendingNavigation | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const applyPresetForm = (form: PresetFormState) => {
    setPresetForm(form);
    setPresetBaseline(presetSignature(form));
  };

  const applyOptimizeForm = (form: OptimizeFormState) => {
    setOptimizeForm(form);
    setOptimizeBaseline(optimizeSignature(form));
  };

  const isDirty = tab === 'optimize'
    ? optimizeSignature(optimizeForm) !== optimizeBaseline
    : presetSignature(presetForm) !== presetBaseline;

  const loadSettings = useCallback(async (activeTab: SettingsTab) => {
    setLoading(true);
    setError(null);
    try {
      const [loadedPresets, loadedSettings] = await Promise.all([
        fetchAllAiImagePresets(),
        fetchAiImagePromptSettings(),
      ]);
      setPresets(loadedPresets);
      setPromptSettings(loadedSettings);

      const optimizeSetting = loadedSettings.find((setting) => setting.key === OPTIMIZE_PROMPT_KEY);
      const nextOptimize = settingToForm(optimizeSetting);
      setOptimizeForm(nextOptimize);
      setOptimizeBaseline(optimizeSignature(nextOptimize));

      const activeScope = scopeForTab(activeTab);
      const firstPreset = loadedPresets.find((preset) => presetScope(preset.scope) === activeScope);
      const nextPreset = firstPreset ? presetToForm(firstPreset) : createEmptyPresetForm(activeScope);
      setPresetForm(nextPreset);
      setPresetBaseline(presetSignature(nextPreset));
    } catch (loadError: any) {
      setError(loadError?.response?.data?.message || loadError?.message || 'Kunde inte ladda promptinställningar.');
    } finally {
      setLoading(false);
    }
  }, []);

  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setTab(initialTab);
      void loadSettings(initialTab);
    }
    wasOpenRef.current = open;
  }, [initialTab, loadSettings, open]);

  const scopePresets = useMemo(() => {
    const scope = scopeForTab(tab);
    return presets.filter((preset) => presetScope(preset.scope) === scope);
  }, [presets, tab]);

  const applyNavigation = (navigation: PendingNavigation) => {
    setError(null);
    setSuccess(null);
    if (navigation.type === 'close') {
      onClose();
      return;
    }
    if (navigation.type === 'tab') {
      setTab(navigation.tab);
      if (navigation.tab !== 'optimize') {
        const firstPreset = presets.find((preset) => presetScope(preset.scope) === navigation.tab);
        applyPresetForm(firstPreset ? presetToForm(firstPreset) : createEmptyPresetForm(navigation.tab));
      }
      return;
    }
    applyPresetForm(navigation.preset ? presetToForm(navigation.preset) : createEmptyPresetForm(scopeForTab(tab)));
  };

  const requestNavigation = (navigation: PendingNavigation) => {
    if (isDirty) {
      setPendingNavigation(navigation);
      return;
    }
    applyNavigation(navigation);
  };

  const handleTabChange = (_event: React.SyntheticEvent, nextTab: SettingsTab) => {
    if (nextTab === tab) return;
    requestNavigation({ type: 'tab', tab: nextTab });
  };

  const handleNewPreset = () => {
    requestNavigation({ type: 'preset', preset: null });
  };

  const handleSavePreset = async () => {
    const payload = presetPayload(presetForm);
    if (!payload.name || !payload.promptTemplate) {
      setError('Namn och prompttext krävs.');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const saved = presetForm._id
        ? await updateAiImagePreset(presetForm._id, payload)
        : await createAiImagePreset(payload);
      const loadedPresets = await fetchAllAiImagePresets();
      setPresets(loadedPresets);
      applyPresetForm(presetToForm(saved));
      await onPresetsChanged();
      setSuccess('Prompten sparades.');
    } catch (saveError: any) {
      setError(saveError?.response?.data?.message || saveError?.message || 'Kunde inte spara prompten.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePreset = async () => {
    if (!presetForm._id) return;
    setDeleteOpen(false);

    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await deleteAiImagePreset(presetForm._id);
      const loadedPresets = await fetchAllAiImagePresets();
      setPresets(loadedPresets);
      const scope = scopeForTab(tab);
      const nextPreset = loadedPresets.find((preset) => presetScope(preset.scope) === scope);
      applyPresetForm(nextPreset ? presetToForm(nextPreset) : createEmptyPresetForm(scope));
      await onPresetsChanged();
      setSuccess('Prompten togs bort permanent.');
    } catch (deleteError: any) {
      setError(deleteError?.response?.data?.message || deleteError?.message || 'Kunde inte radera prompten.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveOptimizePrompt = async () => {
    const payload = optimizePayload(optimizeForm);
    if (!payload.name || !payload.promptText) {
      setError('Namn och prompttext krävs.');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const saved = await updateAiImagePromptSetting(optimizeForm.key, payload);
      const nextSettings = promptSettings.some((setting) => setting.key === saved.key)
        ? promptSettings.map((setting) => (setting.key === saved.key ? saved : setting))
        : [...promptSettings, saved];
      setPromptSettings(nextSettings);
      applyOptimizeForm(settingToForm(saved));
      setSuccess('Prompten för optimering sparades.');
    } catch (saveError: any) {
      setError(saveError?.response?.data?.message || saveError?.message || 'Kunde inte spara promptinställningen.');
    } finally {
      setSaving(false);
    }
  };

  const renderPresetEditor = () => (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '260px minmax(0, 1fr)' }, gap: 2, minHeight: 420 }}>
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
            {tab === 'recipe' ? 'Receptmallar' : tab === 'wine' ? 'Vinprompter' : 'Skapa-prompter'}
          </Typography>
          <Button size="small" startIcon={<AddIcon />} onClick={handleNewPreset} sx={{ textTransform: 'none' }}>
            Ny
          </Button>
        </Box>
        <Divider />
        <List dense disablePadding sx={{ maxHeight: 370, overflow: 'auto' }}>
          {scopePresets.length === 0 && (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Inga promptar finns ännu.
              </Typography>
            </Box>
          )}
          {scopePresets.map((preset) => (
            <ListItemButton
              key={preset._id}
              selected={presetForm._id === preset._id}
              onClick={() => {
                if (presetForm._id === preset._id) return;
                requestNavigation({ type: 'preset', preset });
              }}
            >
              <ListItemText
                primary={preset.name}
                secondary={preset.isActive ? 'Aktiv' : 'Inaktiv'}
                primaryTypographyProps={{ fontWeight: 700, noWrap: true }}
                secondaryTypographyProps={{ color: preset.isActive ? 'success.main' : 'text.disabled' }}
              />
              {!preset.isActive && <Chip label="Inaktiv" size="small" />}
            </ListItemButton>
          ))}
        </List>
      </Box>

      <Stack spacing={1.5}>
        <TextField
          label="Synligt namn"
          value={presetForm.name}
          onChange={(event) => setPresetForm((prev) => ({ ...prev, name: event.target.value }))}
          fullWidth
        />
        <TextField
          label="Prompttext"
          value={presetForm.promptTemplate}
          onChange={(event) => setPresetForm((prev) => ({ ...prev, promptTemplate: event.target.value }))}
          minRows={12}
          multiline
          fullWidth
        />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
          <FormControlLabel
            control={(
              <Switch
                checked={presetForm.isActive}
                onChange={(event) => setPresetForm((prev) => ({ ...prev, isActive: event.target.checked }))}
              />
            )}
            label={presetForm.isActive ? 'Aktiv' : 'Inaktiv'}
          />
          <TextField
            label="Sortering"
            type="number"
            value={presetForm.sortOrder}
            onChange={(event) => setPresetForm((prev) => ({ ...prev, sortOrder: Number(event.target.value) }))}
            sx={{ maxWidth: 160 }}
          />
        </Stack>
      </Stack>
    </Box>
  );

  const renderOptimizeEditor = () => (
    <Stack spacing={1.5} sx={{ minHeight: 420 }}>
      <Alert severity="info">
        Den här prompten används av knappen “Optimera prompt med AI”. Använd <strong>{'{model}'}</strong> i texten om modellnamnet ska injiceras i prompten.
      </Alert>
      <TextField
        label="Synligt namn"
        value={optimizeForm.name}
        onChange={(event) => setOptimizeForm((prev) => ({ ...prev, name: event.target.value }))}
        fullWidth
      />
      <TextField
        label="Prompttext"
        value={optimizeForm.promptText}
        onChange={(event) => setOptimizeForm((prev) => ({ ...prev, promptText: event.target.value }))}
        minRows={16}
        multiline
        fullWidth
      />
    </Stack>
  );

  return (
    <>
    <Dialog open={open} onClose={saving ? undefined : () => requestNavigation({ type: 'close' })} maxWidth="lg" fullWidth>
      <DialogTitle>Promptinställningar</DialogTitle>
      <DialogContent dividers>
        <Tabs value={tab} onChange={handleTabChange} sx={{ mb: 2 }}>
          <Tab value="general" label="Skapa" />
          <Tab value="recipe" label="Förnya receptbild" />
          <Tab value="wine" label="Vin prompt" />
          <Tab value="optimize" label="Optimera prompt" />
        </Tabs>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        {loading ? (
          <Box sx={{ minHeight: 420, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
            <CircularProgress size={24} />
            <Typography>Laddar promptinställningar...</Typography>
          </Box>
        ) : tab === 'optimize' ? renderOptimizeEditor() : renderPresetEditor()}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        {tab !== 'optimize' && presetForm._id && (
          <Button
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setDeleteOpen(true)}
            disabled={saving}
            sx={{ mr: 'auto', textTransform: 'none' }}
          >
            Ta bort permanent
          </Button>
        )}
        <Button onClick={() => requestNavigation({ type: 'close' })} disabled={saving} sx={{ textTransform: 'none' }}>
          Stäng
        </Button>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
          onClick={() => void (tab === 'optimize' ? handleSaveOptimizePrompt() : handleSavePreset())}
          disabled={saving || loading}
          sx={{ textTransform: 'none' }}
        >
          Spara
        </Button>
      </DialogActions>
    </Dialog>
    <StudioActionDialog
      open={Boolean(pendingNavigation)}
      title="Osparade ändringar"
      description="Det finns osparade ändringar i promptmallen. Vill du lämna utan att spara?"
      confirmLabel="Lämna utan att spara"
      cancelLabel="Fortsätt redigera"
      confirmColor="warning"
      onClose={() => setPendingNavigation(null)}
      onConfirm={() => {
        const navigation = pendingNavigation;
        setPendingNavigation(null);
        if (navigation) applyNavigation(navigation);
      }}
    />
    <StudioActionDialog
      open={deleteOpen}
      title="Ta bort promptmall"
      description={`Ta bort "${presetForm.name || 'promptmallen'}" permanent? Det går inte att ångra.`}
      confirmLabel="Ta bort permanent"
      confirmColor="error"
      onClose={() => setDeleteOpen(false)}
      onConfirm={() => { void handleDeletePreset(); }}
    />
    </>
  );
};

export default PromptSettingsDialog;
